import { useEffect, useMemo, useRef, useState } from 'react'
import { API_ENDPOINTS } from '../../config/api'
import { Button } from '../ui/Button'
import styles from '../../styles/components/integrations/PlatformAiProviderAccessModal.module.scss'

export type AccessOrg = { id: string; name: string }

export type AccessGrant = {
  id: string
  organizationId: string
  organizationName?: string | null
  enabled: boolean
  platformProviderId?: string
}

type Props = {
  open: boolean
  token: string
  providerId: string
  providerLabel: string
  /** Enabled grants for this provider (initial selection). */
  initialGrants: AccessGrant[]
  t: (key: string, fallback: string) => string
  onClose: () => void
  /** Called after a fully successful apply (all ops ok). */
  onApplied: () => void
}

type OpFailure = { organizationName: string; action: 'grant' | 'revoke'; message: string }

/**
 * Modal: pick organizations by name for PLATFORM_MANAGED provider access.
 * UUIDs stay internal to API calls only.
 */
export default function PlatformAiProviderAccessModal({
  open,
  token,
  providerId,
  providerLabel,
  initialGrants,
  t,
  onClose,
  onApplied,
}: Props) {
  const [orgs, setOrgs] = useState<AccessOrg[]>([])
  const [loadingOrgs, setLoadingOrgs] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [baselineIds, setBaselineIds] = useState<Set<string>>(new Set())
  const [grantByOrgId, setGrantByOrgId] = useState<Map<string, string>>(new Map())
  const [saving, setSaving] = useState(false)
  const [applyError, setApplyError] = useState<string | null>(null)
  const wasOpenRef = useRef(false)

  const seedFromGrants = (grantList: AccessGrant[]) => {
    const enabled = (grantList || []).filter((g) => g.enabled && g.organizationId)
    const base = new Set(enabled.map((g) => String(g.organizationId)))
    const map = new Map<string, string>()
    for (const g of enabled) {
      map.set(String(g.organizationId), g.id)
    }
    setBaselineIds(base)
    setSelectedIds(new Set(base))
    setGrantByOrgId(map)
  }

  // After partial apply, parent reloads grants — sync baseline/grant ids without wiping checkbox edits
  useEffect(() => {
    if (!open || !wasOpenRef.current) return
    const enabled = (initialGrants || []).filter((g) => g.enabled && g.organizationId)
    const map = new Map<string, string>()
    for (const g of enabled) {
      map.set(String(g.organizationId), g.id)
    }
    setGrantByOrgId(map)
    setBaselineIds(new Set(enabled.map((g) => String(g.organizationId))))
  }, [initialGrants, open])

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false
      return
    }
    const justOpened = !wasOpenRef.current
    wasOpenRef.current = true
    if (!justOpened) return

    seedFromGrants(initialGrants)
    setSearch('')
    setApplyError(null)
    setLoadError(null)

    const loadOrgs = async () => {
      setLoadingOrgs(true)
      try {
        const res = await fetch(API_ENDPOINTS.ADMIN_ORGANIZATIONS, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (res.status === 403) {
          setLoadError(
            t('integrations.ai.platformAdminRequired', 'Platform administrator access required'),
          )
          setOrgs([])
          return
        }
        if (!res.ok) {
          throw new Error(t('integrations.ai.orgsLoadFailed', 'Failed to load organizations'))
        }
        const data = await res.json().catch(() => [])
        const list = Array.isArray(data) ? data : data?.organizations ?? []
        const mapped: AccessOrg[] = list
          .filter((o: { id?: string; name?: string }) => o?.id && o?.name)
          .map((o: { id: string; name: string }) => ({ id: String(o.id), name: String(o.name) }))
          .sort((a: AccessOrg, b: AccessOrg) => a.name.localeCompare(b.name))
        setOrgs(mapped)
      } catch (e: unknown) {
        setLoadError(e instanceof Error ? e.message : 'Failed to load organizations')
        setOrgs([])
      } finally {
        setLoadingOrgs(false)
      }
    }
    void loadOrgs()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed only on open transition
  }, [open, token, providerId, t])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return orgs
    return orgs.filter((o) => o.name.toLowerCase().includes(q))
  }, [orgs, search])

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((o) => selectedIds.has(o.id))

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (allFilteredSelected) {
        for (const o of filtered) next.delete(o.id)
      } else {
        for (const o of filtered) next.add(o.id)
      }
      return next
    })
  }

  const applyChanges = async () => {
    setSaving(true)
    setApplyError(null)
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }

    const toGrant: string[] = []
    const toRevoke: { orgId: string; grantId: string }[] = []

    for (const id of selectedIds) {
      if (!baselineIds.has(id)) toGrant.push(id)
    }
    for (const id of baselineIds) {
      if (!selectedIds.has(id)) {
        const grantId = grantByOrgId.get(id)
        if (grantId) toRevoke.push({ orgId: id, grantId })
      }
    }

    if (toGrant.length === 0 && toRevoke.length === 0) {
      setSaving(false)
      onClose()
      return
    }

    const failures: OpFailure[] = []
    const nameOf = (orgId: string) => orgs.find((o) => o.id === orgId)?.name || orgId

    for (const orgId of toGrant) {
      try {
        const res = await fetch(API_ENDPOINTS.AI_PLATFORM_PROVIDER_GRANT(providerId), {
          method: 'POST',
          headers,
          body: JSON.stringify({ organizationId: orgId, enabled: true }),
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok) {
          failures.push({
            organizationName: nameOf(orgId),
            action: 'grant',
            message: json?.message || `HTTP ${res.status}`,
          })
        }
      } catch (e: unknown) {
        failures.push({
          organizationName: nameOf(orgId),
          action: 'grant',
          message: e instanceof Error ? e.message : 'Grant failed',
        })
      }
    }

    for (const { orgId, grantId } of toRevoke) {
      try {
        const res = await fetch(API_ENDPOINTS.AI_PLATFORM_PROVIDER_GRANT_REVOKE(grantId), {
          method: 'POST',
          headers,
          body: '{}',
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok) {
          failures.push({
            organizationName: nameOf(orgId),
            action: 'revoke',
            message: json?.message || `HTTP ${res.status}`,
          })
        }
      } catch (e: unknown) {
        failures.push({
          organizationName: nameOf(orgId),
          action: 'revoke',
          message: e instanceof Error ? e.message : 'Revoke failed',
        })
      }
    }

    setSaving(false)

    if (failures.length > 0) {
      const lines = failures.map(
        (f) =>
          `${f.organizationName}: ${f.action === 'grant' ? t('integrations.ai.grant', 'Grant') : t('integrations.ai.revoke', 'Revoke')} — ${f.message}`,
      )
      setApplyError(
        [
          t(
            'integrations.ai.accessPartialFailure',
            'Some access changes failed. The window stays open so you can retry.',
          ),
          ...lines,
        ].join('\n'),
      )
      // Refresh baseline from server so checkboxes reflect reality after partial apply
      onApplied()
      return
    }

    onApplied()
    onClose()
  }

  if (!open) return null

  return (
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="platform-ai-access-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose()
      }}
    >
      <div className={styles.panel}>
        <div className={styles.header}>
          <h2 id="platform-ai-access-title" className={styles.title}>
            {t('integrations.ai.manageAccess', 'Manage access')}
          </h2>
          <p className={styles.subtitle}>
            {providerLabel} —{' '}
            {t('integrations.ai.accessGrantedCount', 'Organizations with access')}:{' '}
            {selectedIds.size}
          </p>
        </div>

        <div className={styles.toolbar}>
          <input
            className={styles.searchInput}
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('integrations.ai.searchOrganizations', 'Search organizations…')}
            aria-label={t('integrations.ai.searchOrganizations', 'Search organizations…')}
            disabled={loadingOrgs || saving}
          />
          <div className={styles.selectAllRow}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={allFilteredSelected}
                onChange={toggleSelectAllFiltered}
                disabled={loadingOrgs || saving || filtered.length === 0}
              />
              <span>{t('integrations.ai.selectAll', 'Select all')}</span>
            </label>
            <span className={styles.countBadge}>
              {selectedIds.size} / {orgs.length}
            </span>
          </div>
        </div>

        <div className={styles.list}>
          {loadingOrgs ? (
            <p className={styles.empty}>{t('common.loading', 'Loading…')}</p>
          ) : loadError ? (
            <p className={styles.empty} role="alert">
              {loadError}
            </p>
          ) : filtered.length === 0 ? (
            <p className={styles.empty}>
              {orgs.length === 0
                ? t('integrations.ai.noOrganizations', 'No organizations found')
                : t('integrations.ai.noSearchMatches', 'No organizations match your search')}
            </p>
          ) : (
            filtered.map((org) => (
              <label key={org.id} className={styles.orgRow}>
                <input
                  type="checkbox"
                  checked={selectedIds.has(org.id)}
                  onChange={() => toggleOne(org.id)}
                  disabled={saving}
                />
                <span className={styles.orgName}>{org.name}</span>
              </label>
            ))
          )}
        </div>

        {applyError ? (
          <div className={styles.errorBox} role="alert">
            {applyError}
          </div>
        ) : null}

        <div className={styles.footer}>
          <Button type="button" size="small" color="outline" onClick={onClose} disabled={saving}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="button"
            size="small"
            color="brand"
            onClick={() => void applyChanges()}
            disabled={saving || loadingOrgs || !!loadError}
          >
            {saving
              ? t('common.saving', 'Saving…')
              : t('integrations.ai.applyAccessChanges', 'Apply changes')}
          </Button>
        </div>
      </div>
    </div>
  )
}
